import { I18n } from "@assets/strings";
import { Range } from "immutable";
import React, { JSX, useEffect, useState } from "react";
import { ResizePayload, useResizeDetector } from "react-resize-detector";
import styled, { css, CSSProperties } from "styled-components";
import { mobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { ArrowNavigator } from "@ui/components/arrowNavigator";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { row } from "@ui/components/layout";
import { Direction, useCarouselInfos } from "./useCarouselInfos";

interface CarouselProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
	hideIfEmpty?: boolean;
	items: T[];
	renderItem: (item: T) => React.ReactNode;
	lastItem?: JSX.Element;
	firstItem?: JSX.Element;
	title?: string | JSX.Element;
	itemWidth?: number;
	itemHeight?: number;
	itemPadding?: number;
	loading: boolean;
	hideArrowNavigation?: boolean;
	isScrollToEndEnabled?: boolean;
	onSeeAllAction?: () => void;
	rowStyle?: CSSProperties;
	forceArrows?: boolean;
	lastPage?: boolean;
	isNews?: boolean;
	insightForClassroom?: boolean;
}

export function Carousel<T>({
	hideIfEmpty = true,
	loading,
	title,
	items,
	lastItem,
	forceArrows,
	firstItem,
	itemWidth = 250,
	itemHeight = 308,
	itemPadding = 12,
	hideArrowNavigation,
	isScrollToEndEnabled,
	onSeeAllAction,
	renderItem,
	rowStyle,
	insightForClassroom = false,
	lastPage,
	isNews,
	...htmlProps
}: CarouselProps<T>): JSX.Element | null {
	const emptyItems = Range(0, 3).toArray();
	const [containerWidth, setContainerWidth] = useState(680);
	const [showSeeAllButton, setShowSeeAllButton] = useState(false);

	const { scrollingDiv, handleScroll, snap, scrollToEnd, isDisabled } = useCarouselInfos(
		containerWidth,
		itemWidth + itemPadding,
		items.length
	);

	useEffect(() => {
		setShowSeeAllButton(
			!!onSeeAllAction && isDisabled("right") && !!scrollingDiv.current && scrollingDiv.current.scrollLeft > 0
		);
	}, [isDisabled, onSeeAllAction, scrollingDiv]);

	useEffect(() => {
		if (isScrollToEndEnabled) {
			scrollToEnd();
		}
	}, [isScrollToEndEnabled, items]);

	const { ref } = useResizeDetector({
		handleHeight: false,
		onResize: (payload: ResizePayload) => payload.width && setContainerWidth(payload.width),
	});

	if (hideIfEmpty && items.length < 1 && !loading && !lastItem && !firstItem) {
		return null;
	}
	return (
		<Container {...htmlProps} ref={ref}>
			<Subtitle>
				<div>{title ? title : ""}</div>
				{(!hideArrowNavigation || forceArrows) && (
					<NavigationRow>
						<StyledArrowNavigator
							previous={isDisabled("left") ? undefined : () => snap("left")}
							next={isDisabled("right") ? undefined : () => snap("right")}
							small
						/>
						{showSeeAllButton && (
							<SeeAllButton>
								<span onClick={onSeeAllAction}>{I18n.t(isNews ? "more_button" : "see_all")}</span>
							</SeeAllButton>
						)}

						{lastPage ? (
							<SeeAllButton>
								<span onClick={onSeeAllAction}>{I18n.t("back")}</span>
							</SeeAllButton>
						) : null}
					</NavigationRow>
				)}
			</Subtitle>
			<Wrapper>
				<Row ref={scrollingDiv} onScroll={handleScroll} style={rowStyle}>
					<Content
						itemWidth={itemWidth}
						itemHeight={itemHeight}
						itemPadding={itemPadding}
						insightForClassroom={insightForClassroom}
					>
						{firstItem}
						{!loading && items ? items.map((item) => renderItem(item)) : emptyItems.map((n) => <Skeleton key={n} />)}
						{lastItem}
					</Content>
					{(["left", "right"] as const).map((dir) => (
						<Fader key={dir} direction={dir} invisible={isDisabled(dir)} />
					))}
				</Row>
			</Wrapper>
		</Container>
	);
}

const Container = styled.div`
	position: relative;
	flex: 1;
	overflow: visible;
`;

const NavigationRow = styled.div`
	${row(0, "flex-end", "center")};
`;

const SeeAllButton = styled(PrimaryMedium)`
	text-align: end;
	color: ${({ theme }) => theme.colors.secondary};
	margin-left: -40px;
	z-index: ${Z.over};
	> span {
		cursor: pointer;
		@media ${mobile} {
			font-size: 0.8125rem;
		}
	}
`;

const Wrapper = styled.div`
	position: relative;
`;

const Subtitle = styled.div`
	${({ theme }) => theme.textStyles.desktopSubtitle};
	${row(10, "space-between", "center")};
	margin-bottom: 16px;
`;

const StyledArrowNavigator = styled(ArrowNavigator)`
	flex: 1;
	flex-shrink: 0;
	width: 75px;
`;

const Row = styled.div`
	overflow: scroll;
	::-webkit-scrollbar {
		display: none; /* Chrome, Safari and Opera */
	}
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
`;

const Content = styled.div.withConfig({
	shouldForwardProp: (prop) => !["itemWidth", "itemHeight", "itemPadding", "insightForClassroom"].includes(prop),
})<{
	itemWidth: number;
	itemHeight: number;
	itemPadding: number;
	insightForClassroom?: boolean;
}>`
	${({ itemPadding }) => row(itemPadding)};

	> * {
		min-width: ${({ itemWidth }) => itemWidth}px;
		max-width: ${({ itemWidth }) => itemWidth}px;
		${({ insightForClassroom, itemHeight }) =>
			insightForClassroom ? `height: ${undefined}` : `height: ${itemHeight}px; `}
		flex: 1;
		flex-shrink: 0;
	}
`;

const Fader = styled.div.withConfig({
	shouldForwardProp: (prop) => !["direction", "invisible"].includes(prop),
})<{ direction: Direction; invisible: boolean }>`
	@media ${mobile} {
		display: none;
	}
	position: absolute;
	width: 200px;
	top: 0;
	bottom: 0;
	pointer-events: none;
	transition: opacity linear 0.2s;
	${({ invisible }) => invisible && "opacity:0"};

	${({ direction }) =>
		direction === "left"
			? css`
					left: 0;
					justify-content: flex-start;
					box-shadow: inset 50px 0px 50px -28px white;
				`
			: css`
					right: 0;
					justify-content: flex-end;
					box-shadow: inset -50px 0px 50px -28px white;
				`}
`;

const Skeleton = styled(RoundedBlock)``;
